import {
        createRouter, createWebHistory, LocationQuery, NavigationGuardWithThis, RouteLocationNormalized,
} from 'vue-router';
import DashboardPage from 'views/DashboardPage.vue';
import OfferingsPage from 'views/OfferingsPage.vue';
import EditOfferingPage from 'views/EditOfferingPage.vue';
// import Home from 'views/Home.vue';
import { authGuard as auth0Guard } from '@auth0/auth0-vue';
import { rememberReturnTo } from '../auth/returnTo';

declare const DEV_BYPASS_AUTH: string | undefined;

const isDevBypass = typeof DEV_BYPASS_AUTH !== 'undefined' && DEV_BYPASS_AUTH === 'true';

// True when the location carries the Auth0 redirect-callback params. During the
// callback the URL temporarily lands on /dashboard (see the root redirect
// below), so we must NOT treat that as the user's intended destination.
function hasAuthCallback(to: { query: LocationQuery }): boolean {
        return Boolean((to.query.code && to.query.state) || to.query.error);
}

// Wrap the Auth0 guard so we remember where the user was heading before being
// redirected to login. After authenticating the SDK returns to the bare origin
// (redirect_uri), so without this the original deep link would be lost and the
// user would always land on /dashboard. `main.ts` restores the remembered path
// once the Auth0 callback has been processed.
const authGuard: NavigationGuardWithThis<undefined> = isDevBypass
        ? () => true
        : (to) => {
                if (!hasAuthCallback(to) && to.path !== '/') {
                        rememberReturnTo(to.fullPath);
                }
                return (auth0Guard as (t: RouteLocationNormalized) => Promise<boolean>)(to);
        };
import PricingPage from 'views/PricingPage.vue';
import ProductsOrderedPage from 'views/ProductsOrderedPage.vue';
import ShowOrderPage from 'views/ShowOrderPage.vue';
import OrderItemPage from 'views/OrderItemPage.vue';
import UserPropertiesPage from 'views/UserPropertiesPage.vue';
import BadgesPage from 'views/BadgesPage.vue';
import EditBadgePage from 'views/EditBadgePage.vue';
import PDPsPage from 'views/PDPsPage.vue';
import EditPDPPage from 'views/EditPDPPage.vue';
import Model3DsPage from 'views/Model3DsPage.vue';
import EditModel3DPage from 'views/EditModel3DPage.vue';
import Model2DsPage from 'views/Model2DsPage.vue';
import EditModel2DPage from 'views/EditModel2DPage.vue';
import StickersPage from 'views/StickersPage.vue';
import EditStickerPage from 'views/EditStickerPage.vue';
import ListerItemsPage from 'views/ListerItemsPage.vue';
import HyperlinksPage from 'views/HyperlinksPage.vue';
import EditHyperlinksPage from 'views/EditHyperlinksPage.vue';
import RegionsPage from 'views/RegionsPage.vue';
import EditRegionPage from 'views/EditRegionPage.vue';
import CurrenciesPage from 'views/CurrenciesPage.vue';
import EditCurrencyPage from 'views/EditCurrencyPage.vue';
import CountriesPage from 'views/CountriesPage.vue';
import EditCountryPage from 'views/EditCountryPage.vue';
import AppSettingsPage from 'views/AppSettingsPage.vue';
import ApiPermissionsPage from 'views/ApiPermissionsPage.vue';
import EditApiPermissionPage from 'views/EditApiPermissionPage.vue';
import CrossSellsPage from 'views/CrossSellsPage.vue';
import EditCrossSellPage from 'views/EditCrossSellPage.vue';
import UpsellsPage from 'views/UpsellsPage.vue';
import PhotoframesPage from 'views/PhotoframesPage.vue';
import EditPhotoframePage from 'views/EditPhotoframePage.vue';
import ThemesPage from 'views/ThemesPage.vue';
import EditThemePage from 'views/EditThemePage.vue';
import ThemePageEditPage from 'views/ThemePageEditPage.vue';
import PageObjectEditPage from 'views/PageObjectEditPage.vue';
import LayoutsPage from 'views/LayoutsPage.vue';
import EditLayoutPage from 'views/EditLayoutPage.vue';
import ThemeCategoriesPage from 'views/ThemeCategoriesPage.vue';
import EditThemeCategoryPage from 'views/EditThemeCategoryPage.vue';
import TemplatesPage from 'views/TemplatesPage.vue';
import EditTemplatePage from 'views/EditTemplatePage.vue';
import EditTemplateStatePage from 'views/EditTemplateStatePage.vue';
import EditTemplatePositionPage from 'views/EditTemplatePositionPage.vue';
import PageBackgroundsPage from 'views/PageBackgroundsPage.vue';
import EditPageBackgroundPage from 'views/EditPageBackgroundPage.vue';
import BulkDiscountsPage from 'views/BulkDiscountsPage.vue';
import EditBulkDiscountPage from 'views/EditBulkDiscountPage.vue';
import DiscountsPage from 'views/DiscountsPage.vue';
import EditDiscountPage from 'views/EditDiscountPage.vue';
import DiscountVouchersPage from 'views/DiscountVouchersPage.vue';
import ShippingPage from 'views/ShippingPage.vue';
import OfferingOptionsPage from 'views/OfferingOptionsPage.vue';
import EditOfferingOptionPage from 'views/EditOfferingOptionPage.vue';
import FlexGroupPage from 'views/FlexGroupPage.vue';
import EditFlexGroupPage from 'views/EditFlexGroupPage.vue';
import LayoutBackend from '../components/layouts/variations/Backend.vue';

const router = createRouter({
        history: createWebHistory('/'),
        routes: [
                {
                        path: '/',
                        component: LayoutBackend,
                        beforeEnter: authGuard,
                        children: [
                                {
                                        path: '',
                                        name: 'home',
                                        // Normally the index route redirects to the dashboard.
                                        // But on an Auth0 redirect callback the URL carries
                                        // `?code`/`state` (or `?error`) params that the SDK must
                                        // read to complete login. A plain string redirect to
                                        // `/dashboard` would drop those params before the SDK
                                        // consumes them, leaving the user unauthenticated and
                                        // causing an infinite login redirect loop in production.
                                        // Preserve the query so the callback params survive; the
                                        // SDK then cleans the URL and navigates to the post-login
                                        // target itself.
                                        redirect: (to) => (hasAuthCallback(to)
                                                ? { path: '/dashboard', query: to.query }
                                                : { path: '/dashboard' }),
                                },
                                {
                                        path: 'dashboard',
                                        name: 'Dashboard',
                                        component: DashboardPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/offerings',
                                        name: 'offerings',
                                        component: OfferingsPage,
                                        beforeEnter: authGuard,

                                },
                                {
                                        path: '/offerings/:id',
                                        name: 'editOffering',
                                        component: EditOfferingPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/pricing',
                                        name: 'pricing',
                                        component: PricingPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/products-ordered',
                                        name: 'ProductsOrdered',
                                        component: ProductsOrderedPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/showOrder/:orderid',
                                        name: 'showOrder',
                                        component: ShowOrderPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/orderItem/:orderItemId',
                                        name: 'orderItem',
                                        component: OrderItemPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/appuser/:id',
                                        name: 'UserProperties',
                                        component: UserPropertiesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/badges',
                                        name: 'Badges',
                                        component: BadgesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/badges/:id',
                                        name: 'EditBadges',
                                        component: EditBadgePage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/offeringoption',
                                        name: 'OfferingOption',
                                        component: OfferingOptionsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/offeringoption/:id',
                                        name: 'EditOfferingOption',
                                        component: EditOfferingOptionPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/pdp',
                                        name: 'Pdp',
                                        component: PDPsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/pdp/:id',
                                        name: 'EditPdp',
                                        component: EditPDPPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/model2d',
                                        name: 'Model2d',
                                        component: Model2DsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/model2d/:id',
                                        name: 'EditModel2d',
                                        component: EditModel2DPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/model3d',
                                        name: 'Model3Ds',
                                        component: Model3DsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/model3d/:id',
                                        name: 'EditModel3D',
                                        component: EditModel3DPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/sticker',
                                        name: 'Stickers',
                                        component: StickersPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/sticker/:id',
                                        name: 'EditSticker',
                                        component: EditStickerPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/listeritem',
                                        name: 'ListerItems',
                                        component: ListerItemsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/appsetting/',
                                        name: 'AppSettings',
                                        component: AppSettingsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/application/',
                                        name: 'ApiPermissions',
                                        component: ApiPermissionsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/application/:id',
                                        name: 'EditApiPermission',
                                        component: EditApiPermissionPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/hyperlink',
                                        name: 'Hyperlinks',
                                        component: HyperlinksPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/hyperlink/:id',
                                        name: 'EditHyperlinks',
                                        component: EditHyperlinksPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/region',
                                        name: 'Region',
                                        component: RegionsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/region/:id',
                                        name: 'EditRegion',
                                        component: EditRegionPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/currency',
                                        name: 'Currency',
                                        component: CurrenciesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/currency/:id',
                                        name: 'EditCurrency',
                                        component: EditCurrencyPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/country',
                                        name: 'Country',
                                        component: CountriesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/crosssell',
                                        name: 'CrossSell',
                                        component: CrossSellsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/crosssell/:id',
                                        name: 'EditCrossSell',
                                        component: EditCrossSellPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/upsell',
                                        name: 'Upsells',
                                        component: UpsellsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/flexgroup',
                                        name: 'FlexGroup',
                                        component: FlexGroupPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/flexgroup/:id',
                                        name: 'EditFlexGroup',
                                        component: EditFlexGroupPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/country/:id',
                                        name: 'EditCountry',
                                        component: EditCountryPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/photoframe',
                                        name: 'Photoframe',
                                        component: PhotoframesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/photoframe/:id',
                                        name: 'editPhotoframe',
                                        component: EditPhotoframePage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/themes',
                                        name: 'Themes',
                                        component: ThemesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/theme/:id',
                                        name: 'EditTheme',
                                        component: EditThemePage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/theme/:id/page/:pageid',
                                        name: 'ThemeEditPage',
                                        component: ThemePageEditPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/theme/:id/page/:pageid/object/:objectid',
                                        name: 'PageObject',
                                        component: PageObjectEditPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/layout',
                                        name: 'Layout',
                                        component: LayoutsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/layout/:id',
                                        name: 'EditLayout',
                                        component: EditLayoutPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/themecategory',
                                        name: 'Category',
                                        component: ThemeCategoriesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/themecategory/:id',
                                        name: 'EditCategory',
                                        component: EditThemeCategoryPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/template',
                                        name: 'Template',
                                        component: TemplatesPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/template/:id',
                                        name: 'EditTemplate',
                                        component: EditTemplatePage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/template/:templateid/state/:id',
                                        name: 'EditTemplateState',
                                        component: EditTemplateStatePage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/template/:templateid/state/:id/position/:positionid',
                                        name: 'EditTemplatePosition',
                                        component: EditTemplatePositionPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/pagebg',
                                        name: 'PageBg',
                                        component: PageBackgroundsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/pagebg/:id',
                                        name: 'EditPageBg',
                                        component: EditPageBackgroundPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/discount-bulk',
                                        name: 'BulkDiscount',
                                        component: BulkDiscountsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/discount-bulk/:id',
                                        name: 'EditBulkDiscount',
                                        component: EditBulkDiscountPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/discount',
                                        name: 'Discount',
                                        component: DiscountsPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/discount/:id',
                                        name: 'DiscountEdit',
                                        component: EditDiscountPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/discount-voucher',
                                        name: 'DiscountVoucher',
                                        component: DiscountVouchersPage,
                                        beforeEnter: authGuard,
                                },
                                {
                                        path: '/shipping',
                                        name: 'Shipping',
                                        component: ShippingPage,
                                        beforeEnter: authGuard,
                                },
                        ],
                },
        ],
});
export default router;
